import { JSX } from "react";
import { Col, Divider, Row, Tag } from "antd";
import Text from "../../components/Text/Text";
import color from "../../resource/color";
import { ShopTierCompanyConfig } from "../ShopTier/config";
import { ConfigTierBenefit } from "../ShopTier/mockData";
import TierCardPreview from "./TierCardPreview";
import { EditableTierBenefit } from "./types";

interface Props {
  config: ShopTierCompanyConfig;
  tiers: EditableTierBenefit[];
  cardImageOverrides: Record<string, string>;
  colorOverrides: Record<string, string>;
  originalTiers?: ConfigTierBenefit[];
  changedTierKeys?: string[];
}

function TierReviewList({
  config,
  tiers,
  cardImageOverrides,
  colorOverrides,
  originalTiers = [],
  changedTierKeys = [],
}: Props): JSX.Element {
  return (
    <>
      {config.tiers.map((t) => {
        const tierData = tiers.find((x) => x.tierKey === t.key);
        const benefits = tierData?.benefits || [];
        const percent = tierData?.percent ?? 0;
        const tierColor = colorOverrides[t.key] || t.color;
        const isCustomColor = Boolean(colorOverrides[t.key]);

        const originalTier = originalTiers.find((o) => o.tierKey === t.key);
        const currentTitles = benefits.map((b) => b.title.trim()).filter(Boolean);
        const originalTitles = originalTier?.benefits || [];
        const addedTitles = currentTitles.filter((title) => !originalTitles.includes(title));
        const removedTitles = originalTitles.filter((title) => !currentTitles.includes(title));
        const isEdited = Boolean(originalTier) && changedTierKeys.includes(t.key);
        const changeSummary = isEdited
          ? [
              ...addedTitles.map((title) => `เพิ่ม "${title}"`),
              ...removedTitles.map((title) => `เอาออก "${title}"`),
            ]
          : [];

        return (
          <div key={t.key} style={{
              padding: isEdited ? 24 : 0,
              background: isEdited ? color.background1 : "transparent",
              borderRadius: 24,
              border: isEdited ? `1px solid ${color.border}` : "none",
              marginBottom: 16,
            }}>
            <Row gutter={24} wrap={false}>
              <Col flex="370px">
                <TierCardPreview tier={t} imageOverride={cardImageOverrides[t.key]} width="100%" />
              </Col>
              <Col flex="auto" style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                  <Tag style={{ borderRadius: 16, border: "none", background: tierColor, margin: 0 }}>
                    <Text fontSize={14} fontWeight={700} color="white">{t.label}</Text>
                  </Tag>
                  <Text fontSize={13} color="Text3">{benefits.length} สิทธิประโยชน์</Text>
                  {isEdited && (
                    <Tag style={{ borderRadius: 16, margin: 0, backgroundColor: color.background3, border: "none" }}>
                      <Text fontSize={13} fontWeight={700} color="primary">แก้ไข</Text>
                    </Tag>
                  )}
                </div>
                <Text fontSize={13} color="Text3">
                  {config.percentLabel} Top-up ระดับนี้ {percent}% · พิจารณาทุกร้านในรอบปิดปี · ผู้จัดการตัดสินทุกเคส ระบบไม่ลดเอง
                </Text>
                {changeSummary.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    <Text fontSize={13} color="primary">{changeSummary.join(" · ")}</Text>
                  </div>
                )}

                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                  {benefits.length > 0 ? (
                    benefits.map((b, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: color.background1,
                          borderRadius: 8,
                          padding: "10px 12px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                        }}
                      >
                        <div>
                          <Text fontWeight={700} fontSize={14} color="Text2">{b.title || "-"}</Text>
                          {b.description && (
                            <div>
                              <Text fontSize={13} color="Text3">{b.description}</Text>
                            </div>
                          )}
                        </div>
                        {b.iconUrl ? (
                          <img src={b.iconUrl} alt="" style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0 }} />
                        ) : (
                          <span style={{ width: 28, height: 6, borderRadius: 4, background: color.background2, flexShrink: 0 }} />
                        )}
                      </div>
                    ))
                  ) : (
                    <Text fontSize={13} color="Text3">ยังไม่มีสิทธิประโยชน์ในระดับนี้</Text>
                  )}
                </div>
              </Col>
            </Row>

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, marginBottom: 16 }}>
              <span
                style={{ width: 14, height: 14, borderRadius: "50%", background: tierColor, display: "inline-block", flexShrink: 0 }}
              />
              <Text fontWeight={700} fontSize={13} color="Text2">{tierColor.toUpperCase()}</Text>
              <Text fontSize={12} color="Text3">{isCustomColor ? "กำหนดเอง" : "สีจากทางบริษัท"}</Text>
            </div>
            <Divider />
          </div>
        );
      })}
    </>
  );
}

export default TierReviewList;
